import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    Paper,
    MenuItem,
    Typography,
    FormControlLabel,
    Switch,
    Button,
    Checkbox,
    IconButton,
    Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { green } from "@mui/material/colors";
import Header from "../../components/Header";
import SearchableDataGrid from '../../components/SearchableDataGrid';

const Roles = () => {
    const [formData, setFormData] = useState({
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        active: true,
        type: "Custom",
        personaEnabled: false
    });

    const [rows, setRows] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);

    /* ================= FORM HANDLER ================= */

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    /* ================= ADD ROW ================= */

    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: prev.length + 1,
                functionId: "",
                typeFG: "",
                description: "",
                startDate: "",
                endDate: "",
                active: true
            }
        ]);
    };

    /* ================= DELETE ROW ================= */

    const deleteRow = () => {
        const updated = rows
            .filter((row) => !selectionModel.includes(row.id))
            .map((row, index) => ({
                ...row,
                id: index + 1
            }));

        setRows(updated);
        setSelectionModel([]);
    };

    const processRowUpdate = (newRow) => {
        const updatedRows = rows.map((row) =>
            row.id === newRow.id ? newRow : row
        );
        setRows(updatedRows);
        return newRow;
    };


    /* ================= COLUMNS ================= */

    const columns = [
        { field: "id", headerName: "S No", width: 70 },
        {
            field: "functionId",
            headerName: "Functions ID/Group ID",
            flex: 1,
            editable: true
        },
        {
            field: "typeFG",
            headerName: "Type F/G",
            width: 150,
            editable: true
        },
        {
            field: "description",
            headerName: "Description",
            flex: 1,
            editable: true
        },
        {
            field: "startDate",
            headerName: "Start Date",
            width: 150,
            editable: true,
            type: "date"
        },
        {
            field: "endDate",
            headerName: "End Date",
            width: 150,
            editable: true,
            type: "date"
        },
        {
            field: "active",
            headerName: "Active",
            width: 110,
            renderCell: (params) => (
                <Switch
                    checked={params.value || false}
                    onChange={(e) => {
                        const updatedRows = rows.map((row) =>
                            row.id === params.id
                                ? { ...row, active: e.target.checked }
                                : row
                        );
                        setRows(updatedRows);
                    }}
                    sx={{
                        "& .Mui-checked": { color: green[600] },
                        "& .Mui-checked + .MuiSwitch-track": {
                            backgroundColor: green[600]
                        }
                    }}
                />
            )
        }
    ];

    const handleSave = () => {
        const payload = {
            master: formData,
            details: rows
        };

        console.log("Saving Data:", payload);

        // 👉 Call your API here
        // axios.post("/api/roles", payload)
    };

    const handleReset = () => {
        setFormData({
            name: "",
            description: "",
            startDate: "",
            endDate: "",
            active: true,
            type: "Custom",
            personaEnabled: false
        });

        setRows([]);
        setSelectionModel([]);
    };

    return (
        <Box m="20px">
            <Header title="Create Roles" />

            {/* ================= MASTER FORM ================= */}

            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={3}>
                        <TextField
                            label="Name"
                            name="name"
                            fullWidth
                            value={formData.name}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="Description"
                            name="description"
                            fullWidth
                            value={formData.description}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="Start Date"
                            name="startDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.startDate}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            label="End Date"
                            name="endDate"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            value={formData.endDate}
                            onChange={handleFormChange}
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <TextField
                            select
                            size="small"
                            label="Type"
                            name="type"
                            fullWidth
                            value={formData.type}
                            onChange={handleFormChange}
                        >
                            <MenuItem value="Custom">Custom</MenuItem>
                            <MenuItem value="Seeded">Seeded</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item xs={3} sx={{ my: 1 }}>
                        <FormControlLabel
                            sx={{ mx: 2, gap: 1.5 }}
                            control={
                                <Checkbox
                                    checked={formData.active}
                                    name="active"
                                    onChange={handleFormChange}
                                    sx={{
                                        color: "#bdbdbd", // unchecked color (grey)

                                        "&.Mui-checked": {
                                            color: "#1976d2", // blue when checked
                                        },

                                        "&:hover": {
                                            backgroundColor: "transparent", // remove blue hover background
                                        },
                                    }}
                                />
                            }
                            label="Status"
                        />
                    </Grid>

                    <Grid item xs={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.personaEnabled}
                                    name="personaEnabled"
                                    onChange={handleFormChange}
                                    sx={{
                                        "& .MuiSwitch-switchBase.Mui-checked": {
                                            color: green[600],   // thumb color when ON
                                        },
                                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: green[600],  // track color when ON
                                        },
                                    }}
                                />
                            }
                            label="Persona Enabled"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* ================= ERP TOOLBAR ================= */}


            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                sx={{
                    backgroundColor: '#81a9eb',
                    padding: '10px 0', // Vertical padding for spacing
                    borderRadius: 2,  // Optional: Rounded corners for the toolbar
                }}
                margin='20px 0'
            >
                <Tooltip title="Add Row">
                    <IconButton
                        color="primary"
                        onClick={addRow}
                        sx={{
                            backgroundColor: '#5447a7',  // Orange background
                            color: '#fff',               // White icon color
                            '&:hover': {
                                backgroundColor: '#f57c00',  // Darker orange on hover
                                transform: 'scale(1.1)',      // Slight zoom effect on hover
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)', // More prominent shadow on hover
                            },
                            boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)', // Default shadow
                            borderRadius: '50%',         // Make the icon button circular
                            width: 20,                   // Adjusted icon button size
                            height: 20,
                            position: 'relative',        // Make the button absolutely positioned
                            // top: '10px',                 // Position from the top of the container
                            right: '-450px',
                        }}
                    >
                        <AddIcon sx={{ fontSize: 20 }} />  {/* Adjusting the size of the icon */}
                    </IconButton>
                </Tooltip>

                {/* Delete Row Icon */}
                <Tooltip title="Delete Row">
                    <IconButton
                        color="secondary"
                        onClick={deleteRow}  // You should define the deleteRow function
                        sx={{
                            backgroundColor: '#5447a7',  // Orange background
                            color: '#fff',               // White icon color
                            '&:hover': {
                                backgroundColor: '#f57c00',  // Darker orange on hover
                                transform: 'scale(1.1)',      // Slight zoom effect on hover
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)', // More prominent shadow on hover
                            },
                            boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)', // Default shadow
                            borderRadius: '50%',         // Circular button
                            width: 20,                   // Adjusted icon button size
                            height: 20,
                            position: 'relative',        // Make the button absolutely positioned
                            // top: '10px',                 // Position from the top of the container
                            right: '-500px',               // Adjusted icon button size
                        }}
                    >
                        <DeleteIcon sx={{ fontSize: 20 }} />  {/* Icon size */}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* ================= DATA GRID ================= */}

            <Box
                sx={{
                    backgroundColor: "#f5f7fa",   // same as Adapter
                    padding: 2,
                    borderRadius: 2
                }}
            >
                <SearchableDataGrid
                    rows={rows}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5]}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={(newSelection) =>
                        setSelectionModel(newSelection)
                    }
                    processRowUpdate={processRowUpdate}
                    experimentalFeatures={{ newEditingApi: true }}
                    sx={{
                        backgroundColor: "#ffffff",
                        borderRadius: 2
                    }}
                />
            </Box>

            {/* ================= SAVE / RESET SECTION ================= */}

            <Box
                display="flex"
                justifyContent="flex-start"
                gap={2}
                mt={3}
            >
                <Button
                    variant="outlined"
                    onClick={handleSave}
                    sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        fontWeight: 500,
                        px: 4,
                        "&:hover": {
                            backgroundColor: "#1565c0"
                        }
                    }}
                >
                    Save
                </Button>

                <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{
                        backgroundColor: "#1976d2",
                        textTransform: "none",
                        fontWeight: 500,
                        px: 4,
                        "&:hover": {
                            backgroundColor: "#1565c0"
                        }
                    }}
                >
                    Reset
                </Button>
            </Box>
        </Box>
    );
};

export default Roles;